import React, { useEffect, useMemo, useState } from 'react'
import { ScreenHeader } from 'components'
import { useNavigate, useParams } from 'react-router-dom'
import { GetCompetitionById } from 'hooks'
import { notification, Tabs } from 'antd'
import {
  CompetitionsRoundsTabAddEdit,
  CompetitionsTabAddEdit,
} from 'page-components'
import {
  CommonConstant,
  CompetitionService,
  CompetitionTabScreen,
  DateUtility,
  processMediaList,
} from 'utility'
import { HeaderWrapper } from 'screens/dashboard'

export const CompetitionsAddEditScreen = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { data } = GetCompetitionById(id)
  const [submitData, setSubmitData] = useState({})
  const [tab, setTab] = useState('1')
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [winnerSelected, setWinnerSelected] = useState(false)
  const validateCompetition = (competition) => {
    const { rounds } = competition;
    const errors = [];

    if (rounds.length > 0) {
      const roundNames = new Set();

      rounds.forEach((round) => {
        const name = round.name?.trim().toLowerCase();
        if (roundNames.has(name)) {
          errors.push(`Round name should not be the same.`);
        } else {
          roundNames.add(name);
        }
      });

      rounds.forEach((round, index) => {
        const start_date = DateUtility.toDateString(round.start_date);
        const end_date = DateUtility.toDateString(round.end_date);
        const submission_deadline = DateUtility.toDateString(round.submission_deadline);
        const voting_deadline = DateUtility.toDateString(round.voting_deadline);
        let result_announcement = DateUtility.toDateString(round.result_announcement);

        if (result_announcement !== end_date) {
          round.result_announcement = round.end_date;
          result_announcement = end_date;
        }

        if (start_date > end_date) {
          errors.push(`Round ${index + 1}: Start date cannot be after end date.`);
        }

        if (!(start_date <= submission_deadline && submission_deadline <= end_date)) {
          errors.push(`Round ${index + 1}: Video submission deadline must be on or between the start and end dates.`);
        }

        if (!(start_date <= voting_deadline && voting_deadline <= end_date)) {
          errors.push(`Round ${index + 1}: Voting deadline must be on or between the start and end dates.`);
        }

        if (!(start_date <= submission_deadline &&
          submission_deadline <= voting_deadline &&
          voting_deadline <= result_announcement &&
          result_announcement <= end_date)) {
          errors.push(`Round ${index + 1}: Dates must follow correct order (Start ≤ Submission ≤ Voting ≤ Result ≤ End).`);
        }

        if (index > 0) {
          const prevRoundEnd = DateUtility.toDateString(rounds[index - 1].end_date);
          if (prevRoundEnd > start_date) {
            errors.push(`Round ${index + 1}: Cannot overlap with the previous round.`);
          }
        }
      });
    }

    return errors;
  };
  const onNext = async (screen, formdata) => {
    const submitFormData = {
      ...submitData,
      ...formdata,
    }
    let image = []
    if (files.length === 0) {
      notification.error({
        message: 'Image is required',
      })
      return
    }
    setLoading(true)
    image = await processMediaList(files)
    submitFormData.image = image?.[0]?.keyOrUrl || ''
    setSubmitData(submitFormData)
    if (screen === CompetitionTabScreen.Rounds) {
      const invalidRounds = validateCompetition(submitFormData)
      if (invalidRounds.length > 0) {
        notification.error({
          message: "Validation Error",
          description: (
            <div>
              {invalidRounds.map((err, idx) => (
                <div key={idx}>• {err}</div>
              ))}
            </div>
          ),
          duration: 5,
        });
      } else {

        try {
          if (data?._id) {
            await CompetitionService.patch(data._id, {
              ...submitFormData,
              _id: undefined,
              rounds: submitFormData.rounds.map((ele, index) => ({
                ...ele,
                round_number: index + 1,
              })),
            })
          } else {
            await CompetitionService.add({
              ...submitFormData,
              rounds: submitFormData.rounds.map((ele, index) => ({
                ...ele,
                round_number: index + 1,
              })),
            })
          }
          navigate('/app/competitions')
        } catch (error) {
          notification.error({
            description: error?.response?.data?.message?.toString(),
            message: CommonConstant.error,
          })
        } finally {
          setLoading(false)
        }
      }
    }
    setLoading(false)
    if (screen === CompetitionTabScreen.Common) {
      setTab('2')
    }
  }

  useEffect(() => {
    if (data?._id) {
      setSubmitData({
        ...data,
      })
      setWinnerSelected(data?.winners?.length > 0)
    }
  }, [data])
  const items = useMemo(
    () => [
      {
        key: '1',
        label: 'Competitions',
        children: (
          <CompetitionsTabAddEdit
            setFiles={setFiles}
            data={submitData}
            files={files}
            disable={winnerSelected}
            onNext={onNext}
          />
        ),
      },
      {
        key: '2',
        label: 'Rounds',
        children: (
          <CompetitionsRoundsTabAddEdit
            data={submitData}
            onNext={onNext}
            loading={loading}
            disable={winnerSelected}
          />
        ),
      },
    ],
    [submitData, files],
  )

  return (
    <div>
      <HeaderWrapper>
        <ScreenHeader text="Add New Competitions Details" />
      </HeaderWrapper>

      <div className="px-5">
        <div className="tabs-container">
          <Tabs
            defaultActiveKey="1"
            activeKey={tab}
            items={items}
            className="custom-tabs"
            centered
            onChange={key => setTab(key)}
          />
        </div>
      </div>
    </div>
  )
}